// =============================================================================
// sales-reports.ts — Contrato Zod compartilhado dos relatórios comerciais
// (`/api/sales/reports/*`, F35-S12, doc 26 §16).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/reports) e pelo frontend (aba Comercial de /relatorios) —
// nenhum dos dois lados redeclara enums ou o shape de resposta.
//
// Escopo (doc 26 §16 — "Relatórios só agregam dados que o usuário tem
// permissão de acessar"): city-scope aplicado SEMPRE na query (repository),
// nunca no cliente. O filtro `agentId` é o recorte "por pessoa" deste
// domínio — diferente de `packages/shared-schemas/src/reports.ts`
// (`personId` = `users.id`, resolvido para o escopo efetivo da pessoa), aqui
// o filtro é direto em `agents.id` porque todo dado do módulo comercial
// (`contact_attempts.agent_id`, `campaign_members.assigned_agent_id`,
// `contact_appointments.assigned_agent_id`) já é indexado por agente, sem a
// distinção global/city/self de papéis de gestão que o módulo `reports`
// resolve. `sales_reports:read` (migration 0106) não tem variante `:manage`
// — é uma permissão plana: quem a tem enxerga tudo dentro do seu
// city-scope, filtrável por agente. O city-scope, sozinho, já impede que um
// ator city-scoped filtre por um agente de outra cidade (a junção com
// `sales_campaigns.city_id`/`contact_attempts.city_id` devolve zero linhas).
//
// DECISÃO DE ESCOPO — duração de ligação (doc 26 §16, regra de ouro):
// "produtividade sem usar duração da ligação isoladamente como indicador de
// qualidade". Este contrato NÃO expõe `avgCallDurationSeconds` nem qualquer
// campo de duração — a métrica não existe neste relatório por decisão de
// produto, não por esquecimento.
//
// LGPD (doc 17 §3.3): nenhum campo de PII do titular (lead/cliente) nos
// responses — apenas contagens, somatórios, médias e nomes de
// AGENTES/CAMPANHAS (dado operacional da organização, mesma classe que o
// ranking de produtividade em `reports.ts`).
// =============================================================================
import { z } from 'zod';
import { ReportRangeEnum } from './reports.js';
import { ContactChannelSchema } from './sales.js';
// ---------------------------------------------------------------------------
// Query — GET /api/sales/reports/overview
// ---------------------------------------------------------------------------
const queryUuidArray = z.preprocess((v) => (v === undefined || v === null ? undefined : Array.isArray(v) ? v : [v]), z.array(z.string().uuid()).optional());
export const SalesReportsQuerySchema = z.object({
    range: ReportRangeEnum.optional()
        .default('last30d')
        .describe('Janela de tempo do relatório. Aplicada a tentativas, retornos e taxas — ' +
        'carteira e funil são fotografias do estado atual (não recortadas por período).'),
    dateFrom: z
        .union([z.string().datetime({ offset: true }), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
        .optional()
        .describe('Início do período customizado (`range=custom`). `YYYY-MM-DD` ou ISO 8601 com offset.'),
    dateTo: z
        .union([z.string().datetime({ offset: true }), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
        .optional()
        .describe('Fim do período customizado (`range=custom`).'),
    cityIds: queryUuidArray.describe('Recorte de cidades. Estreita o city-scope do ator — nunca amplia (fora do escopo do ' +
        'ator é 403).'),
    agentId: z
        .string()
        .uuid()
        .optional()
        .describe('Recorte por vendedor (`agents.id`). Fora do city-scope do ator devolve zero linhas, ' +
        'nunca dado de outro agente.'),
    campaignId: z.string().uuid().optional().describe('Recorte por campanha (`sales_campaigns.id`).'),
});
// ---------------------------------------------------------------------------
// Bloco `range` da resposta — mesmo shape de `reports.ts` (não exportado lá).
// ---------------------------------------------------------------------------
export const SalesReportsRangeInfoSchema = z.object({
    from: z.string().describe('Instante inicial (inclusivo), ISO 8601 UTC.'),
    to: z.string().describe('Instante final (inclusivo), ISO 8601 UTC.'),
    label: z.string().describe('Rótulo em pt-BR do período ("Últimos 30 dias").'),
    timeZone: z.string().describe('Fuso IANA usado no cálculo do período (fuso da organização).'),
    fromDay: z.string().describe('Primeiro dia de calendário local do período (YYYY-MM-DD).'),
    toDay: z.string().describe('Último dia de calendário local do período (YYYY-MM-DD).'),
});
// ---------------------------------------------------------------------------
// Carteira — estado atual da fila de campanhas (`campaign_members`)
// ---------------------------------------------------------------------------
export const SalesReportsWalletSchema = z.object({
    total: z.number().int().nonnegative().describe('Total de membros de campanha no escopo.'),
    available: z
        .number()
        .int()
        .nonnegative()
        .describe('Disponível para trabalho agora (`status` pending + available).'),
    inRetorno: z
        .number()
        .int()
        .nonnegative()
        .describe('Aguardando retorno agendado (`status = waiting_callback`).'),
    completed: z
        .number()
        .int()
        .nonnegative()
        .describe('Encerrado com sucesso (`status = completed`).'),
    blocked: z
        .number()
        .int()
        .nonnegative()
        .describe('Encerrado sem sucesso — bloqueado ou removido (`status` blocked + removed).'),
});
// ---------------------------------------------------------------------------
// Tentativas — breakdown de `contact_attempts` no período
// ---------------------------------------------------------------------------
const SalesReportsAttemptsByAgentSchema = z.object({
    agentId: z.string().uuid(),
    agentName: z.string(),
    count: z.number().int().nonnegative(),
});
const SalesReportsAttemptsByCampaignSchema = z.object({
    campaignId: z.string().uuid(),
    campaignName: z.string(),
    count: z.number().int().nonnegative(),
});
const SalesReportsAttemptsByChannelSchema = z.object({
    channel: ContactChannelSchema,
    count: z.number().int().nonnegative(),
});
const SalesReportsAttemptsByDaySchema = z.object({
    day: z.string().describe('Dia de calendário local (YYYY-MM-DD), fuso da organização.'),
    count: z.number().int().nonnegative(),
});
export const SalesReportsAttemptsSchema = z.object({
    total: z.number().int().nonnegative(),
    byAgent: z.array(SalesReportsAttemptsByAgentSchema),
    byCampaign: z.array(SalesReportsAttemptsByCampaignSchema),
    byChannel: z.array(SalesReportsAttemptsByChannelSchema),
    byDay: z.array(SalesReportsAttemptsByDaySchema),
});
// ---------------------------------------------------------------------------
// Taxas — contato, interesse, proposta e conversão (doc 26 §23 item 7)
// ---------------------------------------------------------------------------
export const SalesReportsRatesSchema = z.object({
    contact: z
        .number()
        .nonnegative()
        .describe('% de tentativas com contato efetivo (exclui no_answer/busy/invalid_number).'),
    interest: z
        .number()
        .nonnegative()
        .describe('% de tentativas com desfecho interested/proposal_sent/converted.'),
    proposal: z
        .number()
        .nonnegative()
        .describe('% de tentativas com desfecho proposal_sent/converted.'),
    conversion: z.number().nonnegative().describe('% de tentativas com desfecho converted.'),
});
// ---------------------------------------------------------------------------
// Tempo até primeiro contato
// ---------------------------------------------------------------------------
export const SalesReportsTimeToFirstContactSchema = z.object({
    avgMinutes: z.number().nonnegative().nullable(),
    medianMinutes: z.number().nonnegative().nullable(),
    sampleSize: z
        .number()
        .int()
        .nonnegative()
        .describe('Quantidade de membros de campanha usados no cálculo — 0 = sem amostra.'),
});
// ---------------------------------------------------------------------------
// Retornos (contact_appointments)
// ---------------------------------------------------------------------------
export const SalesReportsCallbacksSchema = z.object({
    marked: z.number().int().nonnegative().describe('Retornos agendados no período.'),
    attended: z.number().int().nonnegative().describe('Retornos concluídos no período.'),
    overdue: z
        .number()
        .int()
        .nonnegative()
        .describe('Retornos atrasados AGORA (status=scheduled e scheduled_at no passado) — fotografia atual, não recortada por período.'),
    rescheduled: z.number().int().nonnegative().describe('Reagendamentos criados no período.'),
});
// ---------------------------------------------------------------------------
// Funil — importados → trabalhados → contatados → convertidos
// ---------------------------------------------------------------------------
export const SalesReportsFunnelSchema = z.object({
    imported: z.number().int().nonnegative(),
    worked: z.number().int().nonnegative(),
    contacted: z.number().int().nonnegative(),
    converted: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Definições de métrica exibidas na UI (tooltip) — DoD do slot.
// ---------------------------------------------------------------------------
export const SalesReportsDefinitionsSchema = z.record(z.string(), z.string());
// ---------------------------------------------------------------------------
// Response — GET /api/sales/reports/overview
// ---------------------------------------------------------------------------
export const SalesReportsOverviewResponseSchema = z.object({
    range: SalesReportsRangeInfoSchema,
    generatedAt: z.string().describe('Instante em que a consulta rodou, ISO 8601 UTC.'),
    stale: z
        .object({
        isStale: z
            .boolean()
            .describe('Sempre `false` nesta camada — consulta direta, sem cache/MV (doc 26 §16).'),
        asOf: z
            .string()
            .describe('Mesmo valor de `generatedAt` — eco explícito para a UI exibir a hora do dado.'),
    })
        .describe('Identificação de frescor do dado (DoD: "dados stale identificados").'),
    definitions: SalesReportsDefinitionsSchema,
    wallet: SalesReportsWalletSchema,
    attempts: SalesReportsAttemptsSchema,
    rates: SalesReportsRatesSchema,
    timeToFirstContact: SalesReportsTimeToFirstContactSchema,
    callbacks: SalesReportsCallbacksSchema,
    funnel: SalesReportsFunnelSchema,
});
// ---------------------------------------------------------------------------
// Export — POST /api/sales/reports/export (CSV, gate reports:export)
// ---------------------------------------------------------------------------
export const SalesReportsExportRequestSchema = SalesReportsQuerySchema.extend({
    format: z.enum(['csv']).describe('Formato do arquivo exportado. Apenas CSV nesta camada.'),
});
//# sourceMappingURL=sales-reports.js.map